import axios from "axios";
import { extractApiErrorMessage, toNumber } from "./safeData.js";

/**
 * Product submission engine.
 *
 * Single write path for every product form in the admin, posting to the FastAPI
 * backend (`POST /api/admin/products`, `PUT /api/admin/products/{id}`). The
 * payload is built explicitly rather than spread from form state, because
 * Pydantic silently drops keys its model does not declare — which is how the
 * checkout payload used to lose the customer's courier choice.
 *
 * @typedef {object} ProductSubmissionFields
 * @property {string} name
 * @property {number|string} source_price
 * @property {number|string} [profit_margin]
 * @property {string} [status]            active | inactive
 * @property {string} [description]
 * @property {string} [marketplace]
 * @property {string} [category]
 * @property {string} [source_url]
 * @property {string} [source_currency]
 * @property {number|string} [weight_lbs]
 * @property {number|string} [estimated_weight_lbs]
 * @property {string[]} [images]
 * @property {boolean} [in_stock]
 *
 * @typedef {object} ProductSubmissionResult
 * @property {boolean} success
 * @property {string} [productId]
 * @property {object} [product]
 * @property {string} [error]
 */

export const PRODUCT_STATUSES = ["active", "inactive"];

/** Mirrors resolve_product_catalog_state on the backend. */
export function resolveCatalogState(fields) {
  const status = (fields?.status || "active").trim().toLowerCase();
  const inStock = fields?.in_stock !== false;
  if (status !== "active") return { status, in_stock: false };
  if (!inStock) return { status: "inactive", in_stock: false };
  return { status: "active", in_stock: true };
}

/** Build the exact body the FastAPI ProductCreate model accepts. */
export function buildProductPayload(fields = {}) {
  return {
    name: (fields.name || "").trim(),
    description: (fields.description || "").trim(),
    source_url: fields.source_url?.trim() || null,
    marketplace: fields.marketplace || "other",
    source_price: toNumber(fields.source_price, 0),
    source_currency: fields.source_currency || "USD",
    category: fields.category || "Electronics",
    weight_lbs: toNumber(fields.weight_lbs),
    estimated_weight_lbs: toNumber(fields.estimated_weight_lbs, 1.0),
    images: Array.isArray(fields.images)
      ? fields.images.filter((url) => typeof url === "string" && url.trim())
      : [],
    profit_margin: toNumber(fields.profit_margin),
    ...resolveCatalogState(fields),
  };
}

/**
 * @returns {string} An empty string when the fields are submittable.
 */
export function validateProductSubmission(fields) {
  if (!(fields?.name || "").trim()) return "Product name is required";

  const price = toNumber(fields.source_price);
  if (price === null) return "Source price is required";
  if (price <= 0) return "Source price must be greater than zero";

  const margin = toNumber(fields.profit_margin);
  if (margin !== null && (margin < 0 || margin > 100)) {
    return "Profit margin must be between 0 and 100 percent";
  }

  const status = (fields.status || "active").trim().toLowerCase();
  if (!PRODUCT_STATUSES.includes(status)) {
    return `Status must be one of: ${PRODUCT_STATUSES.join(", ")}`;
  }

  return "";
}

/**
 * Create or update a product.
 *
 * @param {ProductSubmissionFields} fields
 * @param {{ apiBase: string, productId?: string|null }} context
 * @returns {Promise<ProductSubmissionResult>}
 */
export async function submitProduct(fields, context) {
  const validationError = validateProductSubmission(fields);
  if (validationError) {
    return { success: false, error: validationError };
  }

  const payload = buildProductPayload(fields);
  const productId = context?.productId || null;

  try {
    const response = productId
      ? await axios.put(`${context.apiBase}/admin/products/${productId}`, payload, {
          withCredentials: true,
        })
      : await axios.post(`${context.apiBase}/admin/products`, payload, {
          withCredentials: true,
        });

    return {
      success: true,
      productId: response.data?.product_id || productId,
      product: response.data?.product || null,
    };
  } catch (err) {
    console.error("Product Submission Engine Error:", err);
    return {
      success: false,
      error: extractApiErrorMessage(err, "Failed to submit product to the API"),
    };
  }
}
